import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import adminService from '../../services/adminService'
import PageTitle from '../../components/Common/PageTitle'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'
import UserAvatar from './UserAvatar'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [usageAnalytics, setUsageAnalytics] = useState(null)

  useEffect(() => {
    // Check if user is admin
    if (!adminService.isAdmin()) {
      toast.error('Access denied. Admin privileges required.')
      navigate('/dashboards')
      return
    }

    loadDashboardData()
  }, [navigate])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [dashboardStats, analytics] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getUsageAnalytics()
      ])
      
      setStats(dashboardStats)
      setUsageAnalytics(analytics)
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      toast.error('Failed to load admin dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, subtitle, icon, color = 'blue' }) => (
    <div className="bg-white rounded-lg p-6 shadow-sm border">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600 mt-1`}>
            {typeof value === 'number' && value >= 1000 ? (
              <NumberFormat
                value={value}
                displayType="text"
                thousandSeparator={true}
                renderText={(value) => value}
              />
            ) : (
              value
            )}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className={`text-${color}-500 opacity-50`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="p-4">
        <PageTitle title="Admin Dashboard" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-sm border">
              <Skeleton height={20} className="mb-2" />
              <Skeleton height={32} className="mb-1" />
              <Skeleton height={16} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="p-4">
        <PageTitle title="Admin Dashboard" />
        <div className="text-center mt-8">
          <p className="text-gray-500">Failed to load dashboard data</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <PageTitle title="Admin Dashboard" />
        <button
          onClick={loadDashboardData}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
        >
          Refresh Data
        </button>
      </div>

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          subtitle={`${stats.newUsersLast30Days} new in last 30 days`}
          color="blue"
          icon={
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
          }
        />

        <StatCard
          title="Free Users"
          value={stats.freeUsers}
          subtitle={`${((stats.freeUsers / stats.totalUsers) * 100).toFixed(1)}% of total`}
          color="gray"
          icon={
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
            </svg>
          }
        />

        <StatCard
          title="Paid Users"
          value={stats.paidUsers}
          subtitle={`${((stats.paidUsers / stats.totalUsers) * 100).toFixed(1)}% of total`}
          color="green"
          icon={
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.51-1.31c-.562-.649-1.413-1.076-2.353-1.253V5z" clipRule="evenodd" />
            </svg>
          }
        />

        <StatCard
          title="Total Revenue"
          value={
            <NumberFormat
              value={stats.totalRevenue}
              displayType="text"
              thousandSeparator={true}
              prefix="₹"
              renderText={(value) => value}
            />
          }
          subtitle={`₹${stats.monthlyRevenue.toLocaleString()} this month`}
          color="emerald"
          icon={
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Subscription Breakdown */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(stats.subscriptionBreakdown).map(([tier, count], index) => {
              const percentage = stats.totalUsers > 0
                ? ((count / stats.totalUsers) * 100).toFixed(1)
                : '0.0'
              const knownColors = {
                free: 'bg-gray-300',
                pro: 'bg-blue-500',
                business: 'bg-emerald-500'
              }
              // Stable palette for plans the dashboard doesn't statically know about.
              const palette = ['bg-purple-500', 'bg-pink-500', 'bg-amber-500', 'bg-teal-500', 'bg-rose-500']
              const colorClass = knownColors[tier] || palette[index % palette.length]
              return (
                <div key={tier} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${colorClass}`}></div>
                    <span className="text-sm font-medium text-gray-700 capitalize">{tier}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">{count} users</span>
                    <span className="text-xs text-gray-400">({percentage}%)</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Usage Analytics */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Analytics</h3>
          {usageAnalytics && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Actions</span>
                <span className="font-medium">{usageAnalytics.totalActions.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Active Users</span>
                <span className="font-medium">{usageAnalytics.activeUsers}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Avg Usage per User</span>
                <span className="font-medium">{stats.avgUsage}</span>
              </div>
              
              <div className="pt-3 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Top Actions</h4>
                {Object.entries(usageAnalytics.actionCounts)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([action, count]) => (
                    <div key={action} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">{action.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Users</h3>
          <div className="space-y-3">
            {stats.recentUsers.slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <UserAvatar
                    name={user.name}
                    email={user.email}
                    avatarUrl={user.avatar_url}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    user.subscription_tier === 'free' 
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {user.subscription_tier}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => navigate('/admin/users')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View All Users →
            </button>
          </div>
        </div>

        {/* Recent Payments */}
        <div className="bg-white rounded-lg p-6 shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
          <div className="space-y-3">
            {stats.recentPayments.slice(0, 5).map((payment) => (
              <div key={payment.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {payment.users?.name || 'Unknown user'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {payment.plan_id} • {payment.billing_cycle} • {new Date(payment.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-green-600">
                    <NumberFormat
                      value={payment.amount}
                      displayType="text"
                      thousandSeparator={true}
                      prefix="₹"
                      renderText={(value) => value}
                    />
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    payment.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : payment.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {payment.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => navigate('/admin/subscriptions')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View All Payments →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard